import { useContext, useEffect, useState } from 'react';

import {
	DeleteOutlined,
	DownloadOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Flex,
	Input,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tooltip,
	message,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import {
	useDeleteBatch,
	useGetAllCopilotVideos,
	useGetCopilotBatches,
} from '@/api/batch_process';
import MultiButton from '@/components/common/MultiButton';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import client from '@/lib/http/client';
import { PageContext } from '@/providers/PageContextProvider';
import { IResponseError } from '@/ts';

import QADrawer from '../../qa-collections/QADrawer';
import { IPagination } from '../../video-intelligence-on-demand';
import FiltersSection from './../FiltersSection';
import SendBatchDrawer from './SendBatchDrawer';
import columns, { STATUSES } from './data';

export default function CopilotBatch() {
	const navigate = useNavigate();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const {
		setPageTitle,
		openDrawer,
		closeDrawer,
		setIsCopilotCreditsCounterVisible,
	} = useContext(PageContext);
	const [isDownloading, setIsDownloading] = useState(false);
	const [current, setCurrent] = useState<string>('');
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});
	const [sortBy, setSortBy] = useState<'requested_at'>('requested_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const isAutoRefreshOnLocalStorage = localStorage?.getItem(
		'copilot_batch_isAutoRefreshOn',
	);

	const [isAutoRefreshOn, setIsAutoRefreshOn] = useState(
		isAutoRefreshOnLocalStorage === 'true' ? true : false,
	);

	const { data, isLoading, isFetching, refetch } = useGetCopilotBatches(
		{
			page: pagination.page,
			limit: pagination.limit,
			sortBy,
			sortOrder,
		},
		isAutoRefreshOn,
	);
	const { mutate: deleteBatch, isLoading: isDeleteLoading } = useDeleteBatch();

	const total = data?.data?.data?.pagination?.total ?? 1;
	const items = data?.data?.data?.items ?? [];

	useEffect(() => {
		setPageTitle('Video insights co-pilot');
		setIsCopilotCreditsCounterVisible(true);

		return () => {
			setPageTitle('');
			setIsCopilotCreditsCounterVisible(false);
		};
	}, []);

	useEffect(() => {
		localStorage.setItem(
			'copilot_batch_isAutoRefreshOn',
			isAutoRefreshOn?.toString(),
		);
	}, [isAutoRefreshOn]);

	useEffect(() => {
		refetch();
	}, [sortOrder, pagination]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.copilot)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'requested_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
		}
	};

	const handleDownload = async (batch_id: string) => {
		try {
			setIsDownloading(true);
			setCurrent(batch_id);
			const response = await client.get<any>(
				`/copilot/batch/json/${batch_id}`,
				{
					responseType: 'blob',
				},
			);

			const url = window.URL.createObjectURL(response.data);

			// Create a link element and trigger download
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `data_${batch_id}.zip`);
			document.body.appendChild(link);
			link.click();

			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Download failed:', error);
			const err = error as IResponseError;
			message.error(
				`Download failed: ${err?.message || err?.response?.data?.message}`,
			);
		} finally {
			setIsDownloading(false);
			setCurrent('');
		}
	};

	const onDeleteBatch = (batch_id: string) => {
		setCurrent(batch_id);
		deleteBatch(batch_id, {
			onSuccess: () => {
				message.success('Batch deleted');
				setCurrent('');
			},
			onError: (error: any) => {
				const err = error as IResponseError;
				message.error(
					err?.message ||
						err?.response?.data?.message ||
						'Failed to delete batch',
				);
			},
		});
	};

	return (
		<>
			<Spin spinning={isLoading || isFetching}>
				<div>
					<Flex vertical gap={16} style={{ width: '100%' }}>
						<Flex
							gap={16}
							justify="flex-end"
							align="center"
							style={{ width: '100%' }}
						>
							<Checkbox
								checked={isAutoRefreshOn}
								onChange={(e) => setIsAutoRefreshOn(e?.target?.checked)}
							>
								auto-refresh every 10 seconds
							</Checkbox>
							<Button onClick={() => openDrawer('add-new-copilot-batch')}>
								New batch
							</Button>
							<Tooltip title="Refresh">
								<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
							</Tooltip>
						</Flex>

						<Table
							loading={isLoading || isFetching}
							// style={{ width: '100%' }}
							onChange={onTableChange}
							dataSource={items}
							columns={[
								...columns,
								{
									title: 'Actions',
									dataIndex: 'actions',
									key: 'actions',
									width: 40,
									ellipsis: false,
									fixed: 'right',
									// flex: true,
									render: (_: any, record: any) => {
										const statusSet = [
											...new Set<number | null>([
												...record.videos.map((v: any) => v.status || null),
											]),
										];
										const anyReady = statusSet?.some(
											(status) => status === STATUSES['Analysis complete'],
										);

										return (
											<Flex
												justify="center"
												align="center"
												style={{ gap: '8px' }}
											>
												<Tooltip title="Download batch data">
													<Button
														disabled={
															!anyReady ||
															(isDownloading && current === record.batch_id)
														}
														loading={
															isDownloading && current === record.batch_id
														}
														// type="link"
														onClick={() => handleDownload(record.batch_id)}
													>
														<DownloadOutlined />
													</Button>
												</Tooltip>
												{localStorage?.getItem(
													'sv_can_user_archive_copilot_posts',
												) === '1' && (
													<Tooltip title="Archive batch">
														<Popconfirm
															title="Sure to archive batch?"
															onConfirm={() => onDeleteBatch(record.batch_id)}
															okText="Yes"
															cancelText="Cancel"
														>
															<Button
																danger
																disabled={
																	isDeleteLoading && current === record.batch_id
																}
																loading={
																	isDeleteLoading && current === record.batch_id
																}
																// type="link"
															>
																<DeleteOutlined />
															</Button>
														</Popconfirm>
													</Tooltip>
												)}
											</Flex>
										);
									},
								},
							]}
							rowKey={'id'}
							bordered
							scroll={{ x: 1100 }}
							pagination={{
								total,
								showSizeChanger: true,
								current: pagination.page,
								pageSizeOptions: [10, 25, 50],
								onChange: (page: number, pageSize: number) => {
									setPagination((cur) => ({ ...cur, page, limit: pageSize }));
								},
							}}
						/>
					</Flex>
				</div>
				<SendBatchDrawer />
			</Spin>
		</>
	);
}
